/**
 * Recognises `NotFoundError` DOM-mutation crashes that come from an EXTERNAL
 * mutation of the page — overwhelmingly a browser translation tool — NOT from
 * Panda code, so Sentry can drop them before they file as shell crashes.
 *
 * Translation extensions and Chrome's built-in translate rewrite the page's
 * text nodes (wrapping them in `<font>` elements). React still holds references
 * to the ORIGINAL nodes, so its next commit calls `removeChild` / `insertBefore`
 * against a node the extension already moved, and the browser throws:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *     The node to be removed is not a child of this node.
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node':
 *     The node before which the new node is to be inserted is not a child…
 *
 * These are the canonical React-vs-translation errors (facebook/react#11538).
 * They surface deep in react-dom's reconciler with NO Panda frame owning the
 * mutation, so `error-origin.ts` classifies them as `panda-shell` and they pile
 * into the shell issue stream (Sentry JAVASCRIPT-NEXTJS-1F / 17) even though the
 * page is being mutated by software Panda does not control. The runtime guard
 * (`dom-mutation-guard.ts`) stops them crashing the shell; this stops the
 * residual reports polluting error analysis and burning quota.
 *
 * Both halves are REQUIRED to match — the DOM method AND the "not a child of
 * this node" clause — so a genuine Panda DOM bug (a different removeChild
 * failure, or a NotFoundError from another Web API) is never silenced by
 * accident.
 *
 * Pure and dependency-free (no Sentry import) so it is trivially unit-testable
 * and reusable on any capture path. Mirrors `in-app-browser-noise.ts` /
 * `error-origin.ts`.
 */

export const DOM_MUTATION_EXTERNAL_ORIGIN = "browser-dom-mutation" as const;

interface DomNoiseExceptionValue {
  type?: string;
  value?: string;
}

export interface DomMutationNoiseEvent {
  /** Sentry's `Event.message` (string in practice; typed loose for safety). */
  message?: unknown;
  exception?: { values?: DomNoiseExceptionValue[] };
}

// The two canonical failures. `[\s\S]*` bridges the method name and the
// "not a child" clause across the rest of the DOMException message.
const REMOVE_CHILD_RE = /removeChild[\s\S]*not a child of this node/i;
const INSERT_BEFORE_RE = /insertBefore[\s\S]*not a child of this node/i;

function matchesExternalDomMutation(text: string): boolean {
  return REMOVE_CHILD_RE.test(text) || INSERT_BEFORE_RE.test(text);
}

/**
 * True when the event is an external DOM-mutation `NotFoundError` (translation
 * tool / extension rewriting the tree) that should be dropped from reporting.
 */
export function isExternalDomMutationNoise(
  event: DomMutationNoiseEvent | null | undefined,
): boolean {
  if (!event) return false;

  for (const value of event.exception?.values ?? []) {
    const combined = `${value.type ?? ""}: ${value.value ?? ""}`;
    if (matchesExternalDomMutation(combined)) return true;
  }

  // Fallback for events carrying only a top-level message (no exception values).
  const message = typeof event.message === "string" ? event.message : "";
  return matchesExternalDomMutation(message);
}
